import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiPost } from "@/lib/apiClient";
import { parseCsv } from "@/lib/parseCsv";

const ACCOUNT_FIELDS = [
  { key: "name", label: "Account Name" },
  { key: "domain", label: "Domain" },
  { key: "website", label: "Website" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "industry", label: "Industry" },
  { key: "source", label: "Lead Source" },
  { key: "sourceDetail", label: "Source Detail" },
  { key: "status", label: "Status" },
  { key: "notes", label: "Notes" },
] as const;

const LEAD_FIELDS = [
  { key: "companyName", label: "Company Name" },
  { key: "domain", label: "Domain" },
  { key: "website", label: "Website" },
  { key: "industry", label: "Industry" },
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "title", label: "Title" },
  { key: "source", label: "Lead Source" },
  { key: "sourceDetail", label: "Source Detail" },
] as const;

function guessMapping(headers: string[], fields: readonly { key: string; label: string }[]) {
  const map: Record<string, string> = {};
  const normalized = headers.map((h) => h.toLowerCase().replace(/[^a-z0-9]/g, ""));
  for (const field of fields) {
    const keyNorm = field.key.toLowerCase();
    const labelNorm = field.label.toLowerCase().replace(/[^a-z0-9]/g, "");
    const idx = normalized.findIndex((h) => h === keyNorm || h === labelNorm || h.includes(keyNorm));
    if (idx >= 0) map[field.key] = headers[idx];
  }
  return map;
}

export function CrmImportPage() {
  const [searchParams] = useSearchParams();
  const initialTarget = searchParams.get("target") === "leads" ? "leads" : "accounts";
  const [target, setTarget] = useState<"accounts" | "leads">(initialTarget);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fields = target === "leads" ? LEAD_FIELDS : ACCOUNT_FIELDS;

  const handleFile = async (file: File | null) => {
    setError(null);
    setResult(null);
    if (!file) return;
    const text = await file.text();
    const parsed = parseCsv(text);
    if (!parsed.headers.length) {
      setError("That CSV has no header row.");
      return;
    }
    setHeaders(parsed.headers);
    setRows(parsed.rows);
    setMapping(guessMapping(parsed.headers, fields));
  };

  const preview = useMemo(() => rows.slice(0, 5), [rows]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);
    if (!rows.length) {
      setError("Choose a CSV file first.");
      return;
    }
    const nameField = target === "leads" ? "companyName" : "name";
    if (!mapping[nameField] && !(target === "leads" && (mapping.firstName || mapping.lastName))) {
      setError(target === "leads" ? "Map Company Name or a contact name." : "Map Account Name.");
      return;
    }
    setIsSubmitting(true);
    const payloadRows = rows.map((row) => {
      const obj: Record<string, string> = {};
      for (const field of fields) {
        const header = mapping[field.key];
        if (!header) continue;
        const idx = headers.indexOf(header);
        if (idx >= 0) obj[field.key] = row[idx] || "";
      }
      if (target === "leads" && obj.companyName) obj.name = obj.companyName;
      return obj;
    });
    const res = await apiPost<{ created?: number; skipped?: number; errors?: string[]; error?: string }>(
      "/api/crm/import",
      { target, rows: payloadRows },
    );
    setIsSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Import failed.");
      return;
    }
    const extra = res.data?.errors?.length ? ` First errors: ${res.data.errors.slice(0, 3).join("; ")}` : "";
    setResult(`Imported ${res.data?.created ?? 0} records. Skipped ${res.data?.skipped ?? 0}.${extra}`);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Import</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Upload a CSV, map columns, and create accounts or leads in this org.
          </p>
        </div>
        <Link
          to="/crm/accounts"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
        >
          Back to accounts
        </Link>
      </div>

      {error ? (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      ) : null}
      {result ? (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{result}</div>
      ) : null}

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
        <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
          <h2 className="mb-3 text-lg font-semibold">File</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="font-medium">Import into</span>
              <select
                value={target}
                onChange={(e) => {
                  const next = e.target.value === "leads" ? "leads" : "accounts";
                  setTarget(next);
                  setMapping(guessMapping(headers, next === "leads" ? LEAD_FIELDS : ACCOUNT_FIELDS));
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
              >
                <option value="accounts">Accounts</option>
                <option value="leads">Leads</option>
              </select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium">CSV file</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => void handleFile(e.target.files?.[0] || null)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
              />
            </label>
          </div>
        </section>

        {headers.length > 0 ? (
          <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
            <h2 className="mb-3 text-lg font-semibold">Map columns</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {fields.map((field) => (
                <label key={field.key} className="space-y-1 text-sm">
                  <span className="font-medium">{field.label}</span>
                  <select
                    value={mapping[field.key] || ""}
                    onChange={(e) => setMapping((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                  >
                    <option value="">Skip</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </section>
        ) : null}

        {preview.length > 0 ? (
          <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
            <h2 className="mb-3 text-lg font-semibold">Preview</h2>
            <div className="overflow-x-auto text-xs">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    {headers.map((h) => (
                      <th key={h} className="border px-2 py-1 text-left font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => (
                    <tr key={i}>
                      {headers.map((_, j) => (
                        <td key={j} className="border px-2 py-1">
                          {row[j] || ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        <button
          type="submit"
          disabled={isSubmitting || !rows.length}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {isSubmitting ? "Importing…" : "Run import"}
        </button>
      </form>
    </div>
  );
}
